import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle, ArrowRight } from "lucide-react";
import api from "../api";

/**
 * Signup success page — landing target after Stripe Checkout redirects.
 * Polls /api/billing/checkout/status/{session_id} every 2s until tenant_provisioned.
 * Times out after ~45s with a graceful "your tenant is being created, check email".
 */
const POLL_MS = 2000;
const POLL_MAX = 23; // ≈ 45s total

const SignupSuccess = () => {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState({ phase: "polling" });

    useEffect(() => {
        if (!sessionId) {
            setStatus({ phase: "missing" });
            return;
        }
        let attempt = 0;
        let cancelled = false;
        const tick = async () => {
            attempt++;
            try {
                const r = await api.get(`/billing/checkout/status/${sessionId}`);
                if (cancelled) return;
                if (r.data?.tenant_provisioned) {
                    setStatus({
                        phase: "done",
                        slug: r.data.tenant_slug,
                        meta: r.data.metadata || {},
                    });
                    return;
                }
                if (attempt >= POLL_MAX) {
                    setStatus({ phase: "timeout", slug: r.data?.tenant_slug });
                    return;
                }
                setTimeout(tick, POLL_MS);
            } catch (e) {
                if (cancelled) return;
                if (e?.response?.status === 404) {
                    setStatus({ phase: "missing" });
                    return;
                }
                if (attempt >= POLL_MAX) {
                    setStatus({ phase: "error" });
                    return;
                }
                setTimeout(tick, POLL_MS);
            }
        };
        tick();
        return () => { cancelled = true; };
    }, [sessionId]);

    return (
        <div
            data-testid="signup-success-page"
            className="min-h-screen bg-[#0f0f12] text-white grain flex items-center justify-center px-6"
        >
            <div className="max-w-lg w-full bg-white/[0.04] backdrop-blur border border-white/10 rounded-3xl p-9 text-center">
                {status.phase === "polling" && (
                    <>
                        <Loader2 className="w-12 h-12 animate-spin text-orange-400 mx-auto" />
                        <h1 className="font-serif font-black text-3xl mt-5">Provisioning your restaurant…</h1>
                        <p className="text-white/60 mt-3">
                            Confirming payment with Stripe and creating your branded storefront. This usually takes 5-15 seconds.
                        </p>
                    </>
                )}
                {status.phase === "done" && (
                    <>
                        <CheckCircle2 className="w-14 h-14 text-green-400 mx-auto" />
                        <h1 className="font-serif font-black text-3xl mt-5">Welcome to HeavenlyEats!</h1>
                        <p className="text-white/60 mt-3">
                            Your restaurant <span className="text-white font-semibold">{status.meta?.name || status.slug}</span> is live. Your 14-day free trial has begun.
                        </p>
                        <div className="mt-7 space-y-3">
                            <Link
                                to={`/r/${status.slug}`}
                                data-testid="success-view-store-btn"
                                className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-orange-500 hover:bg-orange-400 text-white font-semibold shadow-xl shadow-orange-900/30 transition-all"
                            >
                                View my store
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </Link>
                            <div className="text-xs text-white/50 mt-4">
                                Public URL: <span className="text-white/80 font-mono">{window.location.origin}/r/{status.slug}</span>
                            </div>
                        </div>
                    </>
                )}
                {status.phase === "timeout" && (
                    <>
                        <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto" />
                        <h1 className="font-serif font-black text-3xl mt-5">Still processing</h1>
                        <p className="text-white/60 mt-3">
                            Payment confirmed but provisioning is taking longer than expected. We'll email you the moment your storefront is ready.
                        </p>
                        <Link to="/" className="inline-block mt-7 text-orange-300 hover:text-orange-200 underline">
                            Back to home
                        </Link>
                    </>
                )}
                {status.phase === "missing" && (
                    <>
                        <AlertTriangle className="w-12 h-12 text-red-400 mx-auto" />
                        <h1 className="font-serif font-black text-3xl mt-5">Session not found</h1>
                        <p className="text-white/60 mt-3">
                            We couldn't find a payment record for this session. If you completed a payment, please check your email or contact support.
                        </p>
                        <Link to="/" className="inline-block mt-7 text-orange-300 hover:text-orange-200 underline">
                            Back to home
                        </Link>
                    </>
                )}
                {status.phase === "error" && (
                    <>
                        <AlertTriangle className="w-12 h-12 text-red-400 mx-auto" />
                        <h1 className="font-serif font-black text-3xl mt-5">Something went wrong</h1>
                        <p className="text-white/60 mt-3">
                            We had trouble checking your payment status. Please refresh in a moment.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
};

export default SignupSuccess;
